import { createSelector } from 'reselect';
import settings from '/@/core/settings';
import { getBrands, getCountries } from '/@/store/public-data/selectors';
import type { AppInstance, User } from '/@/types/core';
import { localeSort } from '/@/utils';

export interface Location {
    id: number;
    name: string;
    children?: Location[];
    code?: string;
    zones: number[];
}
export interface Country extends Location {
    code: string;
}

const getSession = (state) => state.session;

export const getUser = createSelector([getSession], (session) => session.user as User | undefined);

export const getSessionConversion = createSelector([getSession], ({ conversion }) => conversion);

const getLocation = (id: string, countries: Location[], includeChildren = false) => {
    if (!id || !countries?.length) return null;

    const location = countries.find((country) => country.id === id);
    if (!location) return null;

    const { children, ...locationRest } = location;
    return includeChildren ? { children, ...locationRest } : locationRest;
};

export const getSessionCountries = createSelector([getCountries], (countries): Country[] => {
    if (!countries?.length) return [];

    return settings.app.instances
        .map(({ country }) => getLocation(country, countries))
        .filter((country): country is Country => country !== null)
        .sort(localeSort('name'));
});

export const getSessionCountry = createSelector([getSession, getCountries], (session, countries): Country => {
    // biome-ignore lint/style/noNonNullAssertion: session.country is always defined
    return getLocation(session.country, countries) as Country;
});

export const getSessionLanguage = createSelector([getSession], (session) => session.language);
export const getSessionUnits = createSelector([getSession], (session) => session.units);

export const getAppInstance = createSelector([getSession], (session) =>
    settings.app.instances.find((appInstance) => appInstance.country === session.country)
);

export const getNitrogenRecommendationEnabled = createSelector(
    [getSession, getAppInstance],
    (session, appInstance) => appInstance.enableNitogrenRecommendation
);

export const getSessionBrands = createSelector([getAppInstance, getBrands], (appInstance, brands) => {
    return appInstance.brands.reduce(
        (brandsByType, appInstanceBrand) => {
            const brand = (brands || []).find(({ id }) => id === appInstanceBrand.id);
            if (brand) {
                brand.published = appInstanceBrand.published;
                brandsByType[brand.type].push(brand);
            }
            return brandsByType;
        },
        {
            corn: [],
            soy: [],
            weeds: [],
            chemical: [],
            crop_protection: [],
            refuge: [],
        }
    );
});

export const getSessionCrops = createSelector([getSessionBrands], (sessionBrands) => {
    const availableCrops = Object.keys(sessionBrands);
    return settings.choices.crops.filter(({ value }) => availableCrops.includes(value));
});

export const getEnableSpodoptera = (appInstance: AppInstance) => {
    if (appInstance.spodopteraSince && appInstance.spodopteraTo) {
        const sinceDate = new Date(appInstance.spodopteraSince);
        const toDate = new Date(appInstance.spodopteraTo);
        const today = new Date();
        sinceDate.setHours(0, 0, 0, 0);
        toDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return today >= sinceDate && today <= toDate;
    }
    return false;
};

export const getCornHeadToHeadEnabled = createSelector(
    [getUser, getAppInstance],
    (user, appInstance) =>
        user &&
        user.internalProfileEnabled &&
        user.internalAppInstances.includes(appInstance.id) &&
        appInstance.enableCornHeadToHead
);

export const getInternalProfileEnabled = createSelector(
    [getUser, getAppInstance],
    (user, appInstance) =>
        (user?.internalProfileEnabled && user?.internalAppInstances?.includes(appInstance.id)) || false
);

export const getSoyHeadToHeadEnabled = createSelector(
    [getInternalProfileEnabled, getAppInstance],
    (internalProfileEnabled, appInstance) => {
        return internalProfileEnabled && appInstance.enableSoyHeadToHead;
    }
);

export const getShowReceiveNotificationMessages = createSelector(
    [getUser],
    (user) => user && user.type && user.receiveNotifications === null
);

export const getSoyRecommendationEnabled = (state, appInstance) => {
    const allBrands = getBrands(state);
    const brands = appInstance.brands;
    const countryId = appInstance.country;

    return allBrands
        .filter((brand) => brand.type === 'soy' && brand.countryId === countryId)
        .some((item) => brands.some((bran) => bran.id === item.id && bran.published));
};

export const getCornRecommendationEnabled = (state, appInstance) => {
    const allBrands = getBrands(state);
    const brands = appInstance.brands;
    const countryId = appInstance.country;

    return allBrands
        .filter((brand) => brand.type === 'corn' && brand.countryId === countryId)
        .some((item) => brands.some((bran) => bran.id === item.id && bran.published));
};

export const getMapsEnabled = (state) => {
    return getAppInstance(state).enableMaps;
};

export const getNitrogenSmartEnabled = (state) => {
    return getAppInstance(state).enableNitogrenRecommendation;
};

export const getToken = (state) => state.session.token;

export const getEnabledFrostRisk = (state) => {
    return getAppInstance(state).enableFrostRisk;
};

export const getActivitiesEnabled = (state) => getAppInstance(state).enableActivities;

export const getVisitReportEnabled = (state) => getAppInstance(state).enableVisitReport;

export const getEnabledWeedFinder = (state) => {
    return getAppInstance(state).enableWeedFinder;
};

/** Grower account identifier by country: email for UY, CUIT otherwise (AR). */
export const getGrowerIdentifier = (state): 'cuit' | 'email' => {
    const country = getSessionCountry(state);
    return country?.code?.toLowerCase() === 'uy' ? 'email' : 'cuit';
};

export const getHelpCenterEnabled = (state) => {
    const appInstance = getAppInstance(state);
    if (appInstance?.enableHelpCenter === undefined) {
        return false;
    }
    return appInstance.enableHelpCenter;
};
